package pl.michal_cyran.brand_hub.style

import com.varabyte.kobweb.compose.ui.graphics.Color

// ═══════════════════════════════════════════════════════════════════
//  CYAN DESIGN TOKENS — Kotlin / Compose
//  Import CyanColorsLight or CyanColorsDark into your theme.
//  Raw palette values live in CyanPalette — never use them directly
//  in UI; always go through the semantic token data class.
// ═══════════════════════════════════════════════════════════════════


// ─────────────────────────────────────────────
//  RAW PALETTE  (source-of-truth, internal use)
// ─────────────────────────────────────────────
object CyanPalette {
    val cy50  = Color.rgb(236, 254, 255)
    val cy100 = Color.rgb(207, 250, 254)
    val cy200 = Color.rgb(165, 243, 252)
    val cy300 = Color.rgb(103, 232, 249)
    val cy400 = Color.rgb( 34, 211, 238)
    val cy500 = Color.rgb(  6, 182, 212)
    val cy600 = Color.rgb(  8, 145, 178)
    val cy700 = Color.rgb( 14, 116, 144)
    val cy800 = Color.rgb( 21,  94, 117)
    val cy900 = Color.rgb( 22,  78,  99)
}


// ═══════════════════════════════════════════════
//  SEMANTIC TOKEN DATA CLASS
//  Use these names everywhere in your UI code.
// ═══════════════════════════════════════════════
data class CyanColors(

    // ── PAGE / SECTION BACKGROUNDS ───────────────────────────────────
    // Alternate bgSectionA / bgSectionB on consecutive page sections
    // to create visual rhythm without extra colors.
    val bgSectionA:                         Color,  // odd  sections (1, 3, 5 …)
    val bgSectionB:                         Color,  // even sections (2, 4, 6 …)

    // ── HEADER ───────────────────────────────────────────────────────
    // Frosted-glass nav. Pair headerBg with a blur modifier.
    val headerBg:                           Color,
    val headerBorderBottom:                 Color,

    // ── FOOTER ───────────────────────────────────────────────────────
    val footerBg:                           Color,
    val footerText:                         Color,
    val footerTextMuted:                    Color,
    val footerBorderTop:                    Color,

    // ── PRIMARY BRAND COLOR ──────────────────────────────────────────
    // Use primary for buttons, active indicators, icons.
    // primaryHover for :hover / pressed states.
    val primary:                       Color,
    val primaryHover:                  Color,
    val primaryMuted:                  Color,  // softer secondary accent
    val primarySubtle:                 Color,  // tinted fill / selected row bg

    // ── TYPOGRAPHY ───────────────────────────────────────────────────
    val textHeading:                        Color,  // h1, h2, h3
    val textSubheading:                     Color,  // h4, h5, h6
    val textBody:                           Color,  // paragraphs, list items
    val textMuted:                          Color,  // captions, meta, helper text
    val textDisabled:                       Color,
    val textLink:                           Color,
    val textLinkHover:                      Color,
    val textOnPrimary:                      Color,  // text ON a cyan background
    val textOnPrimaryDim:                   Color,  // secondary text on cyan bg

    // ── BORDERS ──────────────────────────────────────────────────────
    val borderDefault:                      Color,  // all idle borders
    val borderHover:                        Color,  // :hover state
    val borderFocus:                        Color,  // :focus / active input ring
    val borderStrong:                       Color,  // explicit accent border

    // ── DEFAULT CARDS ────────────────────────────────────────────────
    // Standard content cards — features, blog posts, team members, etc.
    val cardBg:                             Color,
    val cardBgHover:                        Color,
    val cardTextHeading:                    Color,
    val cardTextBody:                       Color,
    val cardTextHover:                      Color,

    // ── CARD ICON BOX ────────────────────────────────────────────────
    // The small rounded square/circle that wraps an icon on a card.
    val iconBoxBg:                          Color,
    val iconBoxBgHover:                     Color,
    val iconBoxColor:                       Color,  // icon tint
    val iconBoxColorHover:                  Color,

    // ── FEATURED CARDS ───────────────────────────────────────────────
    // High-emphasis cards: pricing "Pro" tier, hero feature spotlight.
    val cardFeaturedBg:                     Color,
    val cardFeaturedBgHover:                Color,
    val cardFeaturedTextHeading:            Color,
    val cardFeaturedTextBody:               Color,
    val cardFeaturedTextMuted:              Color,
    val cardFeaturedBorder:                 Color,
    val cardFeaturedIconBoxBg:              Color,  // inverted — sits on cyan bg
    val cardFeaturedIconBoxColor:           Color,

    // ── BADGES & TAGS ────────────────────────────────────────────────
    val badgeBg:                            Color,
    val badgeText:                          Color,
    val badgeBorder:                        Color,
    val badgeFeaturedBg:                    Color,  // badge ON a featured/cyan card
    val badgeFeaturedText:                  Color,

    // ── CTA BANNER ───────────────────────────────────────────────────
    // Full-width call-to-action strip / section.
    val ctaBannerBg:                        Color,
    val ctaBannerDecoration:                Color,  // subtle overlay blob tint
    val ctaBannerTextHeading:               Color,
    val ctaBannerTextBody:                  Color,
    val ctaButtonBg:                        Color,
    val ctaButtonBgHover:                   Color,
    val ctaButtonText:                      Color,
    val ctaButtonOutlineBorder:             Color,  // secondary outlined button
    val ctaButtonOutlineText:               Color,
    val ctaButtonOutlineBgHover:            Color,

    // ── SHADOW GLOWS ─────────────────────────────────────────────────
    // Use as box-shadow / elevation tints on cards and focal elements.
    val shadowGlowSm:                       Color,  // 0dp  — subtle lift
    val shadowGlowMd:                       Color,  // 4dp  — card hover
    val shadowGlowLg:                       Color,  // 8dp  — modals / drawers
    val shadowGlowXl:                       Color,  // 16dp — featured / hero

    // ── BACKGROUND GLOW GRADIENTS ────────────────────────────────────
    // Decorative radial gradients; use as background-image layers on
    // sections or behind hero content for atmosphere and depth.
    val glowSoftStart:                      Color,  // centre color for soft glow
    val glowFeaturedStart:                  Color,  // glow beneath featured card

    // ── FORM ELEMENTS ────────────────────────────────────────────────
    val inputBg:                            Color,
    val inputBgFocus:                       Color,
    val inputBorder:                        Color,
    val inputBorderHover:                   Color,
    val inputBorderFocus:                   Color,
    val inputText:                          Color,
    val inputPlaceholder:                   Color,
    val inputShadowFocus:                   Color,  // focus ring tint

    // ── DIVIDERS ─────────────────────────────────────────────────────
    val divider:                            Color,
    val dividerStrong:                      Color,
)


// ═══════════════════════════════════════════════
//  LIGHT THEME
// ═══════════════════════════════════════════════
val CyanColorsLight = CyanColors(

    // Page / Section Backgrounds
    bgSectionA                          = Color.rgb(240, 251, 255),
    bgSectionB                          = Color.rgb(230, 247, 255),

    // Header
    headerBg                            = Color.rgba(255, 255, 255, 220),
    headerBorderBottom                  = CyanPalette.cy500.copy(alpha = 26),    // 10 %

    // Footer
    footerBg                            = CyanPalette.cy900,
    footerText                          = CyanPalette.cy100,
    footerTextMuted                     = CyanPalette.cy100.copy(alpha = 153),   // 60 %
    footerBorderTop                     = CyanPalette.cy400.copy(alpha = 51),    // 20 %

    // Primary Brand
    primary                        = CyanPalette.cy500,
    primaryHover                   = CyanPalette.cy400,
    primaryMuted                   = CyanPalette.cy600,
    primarySubtle                  = CyanPalette.cy500.copy(alpha = 31),    // 12 %

    // Typography
    textHeading                         = CyanPalette.cy700,
    textSubheading                      = CyanPalette.cy600,
    textBody                            = CyanPalette.cy800,
    textMuted                           = CyanPalette.cy600,
    textDisabled                        = CyanPalette.cy700.copy(alpha = 97),    // 38 %
    textLink                            = CyanPalette.cy500,
    textLinkHover                       = CyanPalette.cy400,
    textOnPrimary                       = Color.rgb(255, 255, 255),
    textOnPrimaryDim                    = Color.rgba(255, 255, 255, 204),        // 80 %

    // Borders
    borderDefault                       = CyanPalette.cy500.copy(alpha = 38),   // 15 %
    borderHover                         = CyanPalette.cy500.copy(alpha = 89),   // 35 %
    borderFocus                         = CyanPalette.cy500.copy(alpha = 179),  // 70 %
    borderStrong                        = CyanPalette.cy500,

    // Default Cards
    cardBg                              = Color.rgb(255, 255, 255),
    cardBgHover                         = Color.rgb(229, 248, 252),
    cardTextHeading                     = CyanPalette.cy700,
    cardTextBody                        = CyanPalette.cy800,
    cardTextHover                       = CyanPalette.cy700,

    // Card Icon Box
    iconBoxBg                           = CyanPalette.cy500.copy(alpha = 26),   // 10 %
    iconBoxBgHover                      = CyanPalette.cy500.copy(alpha = 46),   // 18 %
    iconBoxColor                        = CyanPalette.cy500,
    iconBoxColorHover                   = CyanPalette.cy400,

    // Featured Cards
    cardFeaturedBg                      = CyanPalette.cy500,
    cardFeaturedBgHover                 = CyanPalette.cy400,
    cardFeaturedTextHeading             = Color.rgb(255, 255, 255),
    cardFeaturedTextBody                = Color.rgba(255, 255, 255, 224),        // 88 %
    cardFeaturedTextMuted               = Color.rgba(255, 255, 255, 163),        // 64 %
    cardFeaturedBorder                  = Color.rgba(255, 255, 255, 51),         // 20 %
    cardFeaturedIconBoxBg               = Color.rgba(255, 255, 255, 46),         // 18 %
    cardFeaturedIconBoxColor            = Color.rgb(255, 255, 255),

    // Badges & Tags
    badgeBg                             = CyanPalette.cy500.copy(alpha = 31),   // 12 %
    badgeText                           = CyanPalette.cy600,
    badgeBorder                         = CyanPalette.cy500.copy(alpha = 51),   // 20 %
    badgeFeaturedBg                     = Color.rgba(255, 255, 255, 51),         // 20 %
    badgeFeaturedText                   = Color.rgb(255, 255, 255),

    // CTA Banner
    ctaBannerBg                         = CyanPalette.cy500,
    ctaBannerDecoration                 = Color.rgba(255, 255, 255, 15),         //  6 %
    ctaBannerTextHeading                = Color.rgb(255, 255, 255),
    ctaBannerTextBody                   = Color.rgba(255, 255, 255, 204),        // 80 %
    ctaButtonBg                         = Color.rgb(255, 255, 255),
    ctaButtonBgHover                    = CyanPalette.cy50,
    ctaButtonText                       = CyanPalette.cy700,
    ctaButtonOutlineBorder              = Color.rgba(255, 255, 255, 128),        // 50 %
    ctaButtonOutlineText                = Color.rgb(255, 255, 255),
    ctaButtonOutlineBgHover             = Color.rgba(255, 255, 255, 26),         // 10 %

    // Shadow Glows (use as elevation / box-shadow tint)
    shadowGlowSm                        = CyanPalette.cy500.copy(alpha = 56),   // 22 %
    shadowGlowMd                        = CyanPalette.cy500.copy(alpha = 71),   // 28 %
    shadowGlowLg                        = CyanPalette.cy400.copy(alpha = 82),   // 32 %
    shadowGlowXl                        = CyanPalette.cy400.copy(alpha = 97),   // 38 %

    // Background Glow Gradients
    glowSoftStart                       = CyanPalette.cy300.copy(alpha = 56),   // 22 % — radial centre
    glowFeaturedStart                   = CyanPalette.cy400.copy(alpha = 140),  // 55 % — below featured card

    // Form Elements
    inputBg                             = Color.rgb(255, 255, 255),
    inputBgFocus                        = Color.rgb(240, 251, 255),
    inputBorder                         = CyanPalette.cy500.copy(alpha = 51),   // 20 %
    inputBorderHover                    = CyanPalette.cy500.copy(alpha = 102),  // 40 %
    inputBorderFocus                    = CyanPalette.cy500,
    inputText                           = CyanPalette.cy800,
    inputPlaceholder                    = CyanPalette.cy600.copy(alpha = 153),  // 60 %
    inputShadowFocus                    = CyanPalette.cy500.copy(alpha = 46),   // 18 %

    // Dividers
    divider                             = CyanPalette.cy500.copy(alpha = 31),   // 12 %
    dividerStrong                       = CyanPalette.cy500.copy(alpha = 64),   // 25 %
)


// ═══════════════════════════════════════════════
//  DARK THEME
// ═══════════════════════════════════════════════
val CyanColorsDark = CyanColors(
    bgSectionA                          = Color.rgb(  8, 12, 16),
    bgSectionB                          = Color.rgb( 13, 17, 23),

    // Header
    headerBg                            = Color.rgba(10, 28, 36, 255),           // 90 %
    headerBorderBottom                  = CyanPalette.cy400.copy(alpha = 31),    // 12 %

    // Footer
    footerBg                            = Color.rgb(6, 18, 24),
    footerText                          = CyanPalette.cy200,
    footerTextMuted                     = CyanPalette.cy200.copy(alpha = 140),   // 55 %
    footerBorderTop                     = CyanPalette.cy400.copy(alpha = 51),    // 20 %

    // Primary Brand
    primary                        = CyanPalette.cy400,
    primaryHover                   = CyanPalette.cy300,
    primaryMuted                   = CyanPalette.cy500,
    primarySubtle                  = CyanPalette.cy400.copy(alpha = 38),    // 15 %

    // Typography
    textHeading                         = CyanPalette.cy100,
    textSubheading                      = CyanPalette.cy200,
    textBody                            = CyanPalette.cy200,
    textMuted                           = CyanPalette.cy400,
    textDisabled                        = CyanPalette.cy300.copy(alpha = 89),    // 35 %
    textLink                            = CyanPalette.cy400,
    textLinkHover                       = CyanPalette.cy300,
    textOnPrimary                       = Color.rgb(255, 255, 255),
    textOnPrimaryDim                    = Color.rgba(255, 255, 255, 204),        // 80 %

    // Borders
    borderDefault                       = CyanPalette.cy400.copy(alpha = 38),   // 15 %
    borderHover                         = CyanPalette.cy400.copy(alpha = 89),   // 35 %
    borderFocus                         = CyanPalette.cy400.copy(alpha = 179),  // 70 %
    borderStrong                        = CyanPalette.cy400,

    // Default Cards
    cardBg                              = Color.rgb( 23, 32, 48),
    cardBgHover                         = Color.rgb( 22,  60,  78),
    cardTextHeading                     = CyanPalette.cy100,
    cardTextBody                        = CyanPalette.cy200,
    cardTextHover                       = CyanPalette.cy100,

    // Card Icon Box
    iconBoxBg                           = CyanPalette.cy400.copy(alpha = 38),   // 15 %
    iconBoxBgHover                      = CyanPalette.cy400.copy(alpha = 64),   // 25 %
    iconBoxColor                        = CyanPalette.cy400,
    iconBoxColorHover                   = CyanPalette.cy300,

    // Featured Cards (same on dark — already high-contrast)
    cardFeaturedBg                      = CyanPalette.cy500,
    cardFeaturedBgHover                 = CyanPalette.cy400,
    cardFeaturedTextHeading             = Color.rgb(255, 255, 255),
    cardFeaturedTextBody                = Color.rgba(255, 255, 255, 224),        // 88 %
    cardFeaturedTextMuted               = Color.rgba(255, 255, 255, 163),        // 64 %
    cardFeaturedBorder                  = Color.rgba(255, 255, 255, 51),         // 20 %
    cardFeaturedIconBoxBg               = Color.rgba(255, 255, 255, 46),         // 18 %
    cardFeaturedIconBoxColor            = Color.rgb(255, 255, 255),

    // Badges & Tags
    badgeBg                             = Color.rgba(34, 211, 238, 38),
    badgeText                           = Color.rgb(34, 211, 238),
    badgeBorder                         = Color.rgba(34,211,238,77),
    badgeFeaturedBg                     = Color.rgba(255, 255, 255, 51),         // 20 %
    badgeFeaturedText                   = Color.rgb(255, 255, 255),

    // CTA Banner
    ctaBannerBg                         = CyanPalette.cy500,
    ctaBannerDecoration                 = Color.rgba(255, 255, 255, 15),         //  6 %
    ctaBannerTextHeading                = Color.rgb(255, 255, 255),
    ctaBannerTextBody                   = Color.rgba(255, 255, 255, 204),        // 80 %
    ctaButtonBg                         = Color.rgb(255, 255, 255),
    ctaButtonBgHover                    = CyanPalette.cy50,
    ctaButtonText                       = CyanPalette.cy700,
    ctaButtonOutlineBorder              = Color.rgba(255, 255, 255, 128),        // 50 %
    ctaButtonOutlineText                = Color.rgb(255, 255, 255),
    ctaButtonOutlineBgHover             = Color.rgba(255, 255, 255, 26),         // 10 %

    // Shadow Glows
    shadowGlowSm                        = CyanPalette.cy500.copy(alpha = 56),   // 22 %
    shadowGlowMd                        = CyanPalette.cy500.copy(alpha = 71),   // 28 %
    shadowGlowLg                        = CyanPalette.cy400.copy(alpha = 82),   // 32 %
    shadowGlowXl                        = CyanPalette.cy400.copy(alpha = 97),   // 38 %

    // Background Glow Gradients
    glowSoftStart                       = CyanPalette.cy500.copy(alpha = 36),   // 14 % — dimmer on dark
    glowFeaturedStart                   = CyanPalette.cy400.copy(alpha = 140),  // 55 %

    // Form Elements
    inputBg                             = Color.rgb( 17,  46,  60),
    inputBgFocus                        = Color.rgb( 22,  60,  78),
    inputBorder                         = CyanPalette.cy400.copy(alpha = 51),   // 20 %
    inputBorderHover                    = CyanPalette.cy400.copy(alpha = 102),  // 40 %
    inputBorderFocus                    = CyanPalette.cy400,
    inputText                           = CyanPalette.cy100,
    inputPlaceholder                    = CyanPalette.cy300.copy(alpha = 128),  // 50 %
    inputShadowFocus                    = CyanPalette.cy400.copy(alpha = 46),   // 18 %

    // Dividers
    divider                             = CyanPalette.cy400.copy(alpha = 31),   // 12 %
    dividerStrong                       = CyanPalette.cy400.copy(alpha = 64),   // 25 %
)
